import React, { useMemo, useState } from 'react';
import { semantic_colors } from '../../foundation';
import { CSSValueWithLength, getZIndex } from '../../styles';
import { RemoveButton } from '../date-picker/DatePickerIcon';
import { DropdownChipLabel } from '../dropdown';
import { DropdownLabel, DropdownLabelProps } from '../dropdown/DropdownLabel';
import { IconCircleXFill } from '../icons/generated';
import { InputSize } from '../input/types';
import { Popover } from '../popover';
import { Stack } from '../stack';
import type { CategoryEntry } from './types';
import { generateOptionsFromKeys } from './utils';

export interface ChipsDropdownOption<OptionValue> {
  label: string;
  value: OptionValue;
}

export interface CategorySelectorDropdownProps<OptionValue> {
  /** @default 300 */
  width?: CSSValueWithLength;
  /** @default getZIndex('dropdown') */
  zIndex?: number;
  /** @default 'medium' */
  size?: InputSize;
  disabled?: boolean;
  /**
   * @deprecated 레거시 categoriesByLevels props 사용시에만 사용해주세요.
   */
  selectedOptions?: Array<ChipsDropdownOption<OptionValue>>;
  /** `multiple`이 `true`일 때 선택된 option 값이 변경되면 호출됩니다. */
  onChange?: (values: Array<OptionValue>) => void;
  placeholder?: string;
  status?: DropdownLabelProps['status'];
  /**
   * Popover 바깥 영역 클릭시 팝업 닫기 여부
   * @default false
   */
  shouldCloseOnOutsideClick?: boolean;
  /**
   * multiple 모드 여부, true일 경우 DropdownChipLabel 사용, false일 경우 DropdownLabel 사용
   * @default false
   */
  multiple?: boolean;
  children?: React.ReactNode;
}

export interface CategorySelectorDropdownChildrenProps {
  multiple?: boolean;
  onChange?: (selectedKeys: Array<string>, isLeaf?: boolean) => void;
  items?: CategoryEntry[];
  selectedKeys?: Array<string>;
}

export const CategorySelectorDropdown = <OptionValue extends string | number>({
  children,
  width = 300,
  zIndex = getZIndex('dropdown'),
  disabled,
  selectedOptions,
  onChange,
  placeholder,
  status,
  size: sizeProp = 'medium',
  shouldCloseOnOutsideClick = false,
  multiple: multipleProp,
}: CategorySelectorDropdownProps<OptionValue>) => {
  const [open, setOpen] = useState(false);
  const [showRemoveIcon, setShowRemoveIcon] = useState(false);

  const childrenItems = useMemo(
    () =>
      React.isValidElement<CategorySelectorDropdownChildrenProps>(children) && 'items' in children.props
        ? children.props.items
        : undefined,
    [children],
  );
  const childrenSelectedKeys = useMemo(
    () =>
      React.isValidElement<CategorySelectorDropdownChildrenProps>(children) && 'selectedKeys' in children.props
        ? children.props.selectedKeys
        : [],
    [children],
  );
  const childrenMultiple =
    React.isValidElement<CategorySelectorDropdownChildrenProps>(children) && 'multiple' in children.props
      ? children.props.multiple
      : undefined;
  const multiple = multipleProp ?? childrenMultiple ?? false;

  const computedSelectedOptions = useMemo(() => {
    if (selectedOptions) {
      return selectedOptions;
    }
    if (childrenItems && childrenSelectedKeys && childrenSelectedKeys.length > 0) {
      return generateOptionsFromKeys(childrenItems, childrenSelectedKeys);
    }
    return [];
  }, [selectedOptions, childrenItems, childrenSelectedKeys]);

  const isSelectedLeaf = !multipleProp && computedSelectedOptions.length > 0;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelectedKeysChange = (selectedKeys: Array<string>, isLeaf?: boolean) => {
    if (!multiple && isLeaf) {
      setOpen(false);
    }
    if (React.isValidElement<CategorySelectorDropdownChildrenProps>(children)) {
      children.props.onChange?.(selectedKeys, isLeaf);
    }
  };

  const renderChipInput = () => {
    const commonProps = {
      expanded: open,
      width,
      placeholder,
      status,
      size: sizeProp,
    };

    if (multiple) {
      return (
        <DropdownChipLabel
          {...commonProps}
          selectedOptions={computedSelectedOptions}
          // @ts-expect-error: TS2322
          onChange={onChange}
        />
      );
    }
    const selectedLabel = computedSelectedOptions.length > 0 ? computedSelectedOptions[0].label : placeholder;
    return (
      <DropdownLabel
        {...commonProps}
        selected={computedSelectedOptions.length > 0}
        onMouseEnter={() => isSelectedLeaf && setShowRemoveIcon(true)}
        onMouseLeave={() => isSelectedLeaf && setShowRemoveIcon(false)}
      >
        <Stack direction='row' align='center' justify='space-between' width='100%'>
          {selectedLabel}
          {showRemoveIcon && (
            <RemoveButton
              color={semantic_colors.content.tertiary}
              onClick={(event) => {
                event.stopPropagation();
                handleSelectedKeysChange([], false);
                setShowRemoveIcon(false);
              }}
            >
              <IconCircleXFill size={16} color={semantic_colors.content.tertiary} />
            </RemoveButton>
          )}
        </Stack>
      </DropdownLabel>
    );
  };

  const clonedChildren = React.isValidElement<CategorySelectorDropdownChildrenProps>(children)
    ? React.cloneElement(children, {
        ...children.props,
        onChange: handleSelectedKeysChange,
        multiple,
      })
    : children;

  return disabled ? (
    renderChipInput()
  ) : (
    <Popover
      arrow={false}
      zIndex={zIndex}
      shouldCloseOnOutsideClick={shouldCloseOnOutsideClick}
      padding={0}
      content={clonedChildren}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
    >
      {renderChipInput()}
    </Popover>
  );
};
